import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './LanguageSelector.css';

// Definisci le lingue disponibili con un nome breve per il pulsante
const languages = [
    { code: 'it', name: 'Italiano', short: 'Italiano', flag: '🇮🇹' },
    { code: 'en', name: 'English', short: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', short: 'Deutsch', flag: '🇩🇪' },
    { code: 'nl', name: 'Nederlands', short: 'Nederlands', flag: '🇳🇱' },
    { code: 'fr', name: 'Français', short: 'Français', flag: '🇫🇷' },
];

const LanguageSelector = ({ onLanguageChange }) => {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownDir, setDropdownDir] = useState('down'); // 'up' or 'down'
    const pickerRef = useRef(null);
    const dropdownRef = useRef(null);

    const currentLanguage = languages.find(l => l.code === i18n.resolvedLanguage) || languages[0];

    const changeLanguage = (lngCode) => {
        i18n.changeLanguage(lngCode);
        setIsOpen(false);
        if (onLanguageChange) {
            onLanguageChange();
        }
    };

    // Effetto per chiudere il menu se si clicca fuori
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Effetto per chiudere il menu con il tasto 'Esc'
    useEffect(() => {
        const handleKeyUp = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        window.addEventListener('keyup', handleKeyUp);
        return () => window.removeEventListener('keyup', handleKeyUp);
    }, []);

    // Effetto per calcolare la direzione del dropdown (sopra o sotto)
    useEffect(() => {
        if (isOpen && dropdownRef.current && pickerRef.current) {
            setTimeout(() => {
                const buttonRect = pickerRef.current.getBoundingClientRect();
                const dropdownRect = dropdownRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - buttonRect.bottom;
                const spaceAbove = buttonRect.top;

                if (spaceBelow < dropdownRect.height + 10 && spaceAbove > spaceBelow) {
                    setDropdownDir('up');
                } else {
                    setDropdownDir('down');
                }
            }, 0);
        }
    }, [isOpen]);

    const arrowSvgPath = (
        <svg className="arrow-svg" viewBox="0 0 16 16"><polygon points="3,5 8,11 13,5 "></polygon></svg>
    );

    return (
        <div ref={pickerRef} className="language-picker-v2">
            <button
                className="current-language-btn"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(prev => !prev)}
                title={currentLanguage.name}
            >
                <span className="flag">{currentLanguage.flag}</span>
                <span className="short-name">{currentLanguage.short}</span>
                {arrowSvgPath}
            </button>
            
            <div 
                ref={dropdownRef}
                className={`language-dropdown ${isOpen ? 'visible' : ''} ${dropdownDir === 'up' ? 'dropdown-up' : 'dropdown-down'}`} 
                role="menu"
            >
                {languages.map(lang => (
                    <button
                        key={lang.code}
                        role="menuitem"
                        className={`lang-option ${i18n.resolvedLanguage === lang.code ? 'active' : ''}`}
                        onClick={() => changeLanguage(lang.code)}
                        disabled={i18n.resolvedLanguage === lang.code}
                        title={lang.name}
                    >
                        <span className="flag">{lang.flag}</span>
                        <span>{lang.name}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default LanguageSelector;